// The public state of the free plan: open, full or closed. One shared query for
// every page that offers or names the plan.
//
// It never blocks a page and never shows a wrong "full" or "closed": while the
// answer loads, and when it fails or times out (2 s), the state is `open`.
import { useQuery } from '@tanstack/react-query'
import { apiV2 } from '@/lib/api-v2-client'
import {
  FREE_PLAN_FALLBACK,
  freeLocationLabel,
  parsePublicFreePlan,
  type FreePlanState,
  type PublicFreePlan,
} from '@/lib/free-plan'

/** How long the state request may take before the page carries on with `open`. */
export const FREE_PLAN_TIMEOUT_MS = 2000

/** Fetch and read the public state. Rejects on timeout, network error and non-2xx. */
export async function fetchFreePlan(signal?: AbortSignal, timeoutMs: number = FREE_PLAN_TIMEOUT_MS): Promise<PublicFreePlan> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  const forward = () => controller.abort()
  signal?.addEventListener('abort', forward)
  try {
    return parsePublicFreePlan(await apiV2.getFreePlan(controller.signal))
  } finally {
    clearTimeout(timer)
    signal?.removeEventListener('abort', forward)
  }
}

export type FreePlanView = PublicFreePlan & {
  isOpen: boolean
  isFull: boolean
  isClosed: boolean
  /** "Nuremberg (free)" */
  locationLabel: string
}

export function describeFreePlan(plan: PublicFreePlan): FreePlanView {
  return {
    ...plan,
    isOpen: plan.state === 'open',
    isFull: plan.state === 'full',
    isClosed: plan.state === 'closed',
    locationLabel: freeLocationLabel(plan.location),
  }
}

export function useFreePlan(): FreePlanView {
  const { data } = useQuery({
    queryKey: ['free-plan'],
    queryFn: ({ signal }) => fetchFreePlan(signal),
    staleTime: 30_000,
    retry: false,
    refetchOnWindowFocus: false,
  })
  return describeFreePlan(data ?? FREE_PLAN_FALLBACK)
}

export type { FreePlanState }
